import React, { useEffect, useMemo, useState } from 'react';
import {
  changeSubUserStatus,
  createSubUser,
  deleteSubUser,
  getSubUsers,
  SaveApiResMessage,
} from '@/actions';
import { Icons } from '@/assets';
import CustomSwitchV2 from '@/components/CostomSwitch/CustomSwitchV2';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/Input';
import { TableV3 } from '@/components/TableV3';
import IconButton from '@/ctComponents/IconButton';
import Modal from '@/ctComponents/Modal';
import { createPortal } from 'react-dom';
import LoadingV2 from '@/components/Loading';

export const Notification = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [email, setEmail] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState({
    show: false,
    data: null,
  });
  const [subUsers, setSubUsers] = useState([]);

  useEffect(() => {
    setIsLoadingData(true);
    getSubUsers()
      .then((res) => {
        setSubUsers(res.data.list || []);
      })
      .finally(() => {
        setIsLoadingData(false);
      });
  }, []);

  const getStatusText = (checked) => {
    switch (checked) {
      case 'CONFIRMED':
        return 'Verified';
      default:
        return 'Pending';
    }
  };

  const getStatusClass = (checked) => {
    switch (checked) {
      case 'CONFIRMED':
        return 'bg-success-light text-success';
      default:
        return 'bg-gray-100 text-inactive-text';
    }
  };

  const getStatusIcon = (checked) => {
    switch (checked) {
      case 'CONFIRMED':
        return Icons.circleCheck;
      default:
        return Icons.halfCircle;
    }
  };

  const handleSave = () => {
    setIsSaving(true);
    createSubUser({ email })
      .then((res) => {
        SaveApiResMessage({
          title: res?.data?.message,
          message: 'Contact added successfully',
          mode: 'success',
        });
        setEmail('');
        setSubUsers(res.data.list || []);
      })
      .catch((err) => {
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'error',
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleChangeStatus = (id, e) => {
    changeSubUserStatus({
      sub_user_id: id,
      receive_email: e ? 'ACTIVE' : 'INACTIVE',
    })
      .then((res) => {
        setSubUsers(res.data.list || []);
        SaveApiResMessage({
          title: res?.data?.message,
          message: 'Status changed successfully',
          mode: 'success',
        });
      })
      .catch((err) => {
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'error',
        });
      });
  };

  const handleDelete = () => {
    deleteSubUser(showDeleteModal.data.id)
      .then((res) => {
        setSubUsers(res.data.list || []);
        setShowDeleteModal({ show: false, data: null });
        SaveApiResMessage({
          title: res?.data?.message,
          message: 'Contact deleted successfully',
          mode: 'success',
        });
      })
      .catch((err) => {
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'error',
        });
      });
  };

  const columns = [
    {
      field: 'id',
      label: '#',
      component: (row, index) => index + 1,
    },

    {
      field: 'email',
      label: 'Email',
    },
    {
      field: 'confirm_status',
      label: 'Status',
      component: (row) => (
        <div
          className={`cursor-pointer flex items-center mx-auto justify-center gap-2 w-fit px-2 py-1 rounded-full ${getStatusClass(
            row.confirm_status
          )}`}>
          <span
            className={
              row.confirm_status !== 'CONFIRMED' ? 'animate-spin' : ''
            }>
            <Icon
              src={getStatusIcon(row.confirm_status)}
              width={16}
              height={16}
            />
          </span>
          {getStatusText(row.confirm_status)}
        </div>
      ),
    },
    {
      field: 'receive_email',
      label: 'Notification Type',
      component: (row) => (
        <div className="flex items-center justify-center">
          <span className="text-secondary-text">
            {row.receive_email === 'ACTIVE' ? 'Email' : 'Email'}
          </span>
        </div>
      ),
    },
    {
      field: 'receive_email',
      label: 'Receive Notifications',
      component: (row) => (
        <div className="flex items-center justify-center">
          <CustomSwitchV2
            checked={row.receive_email === 'ACTIVE'}
            onChange={(e) => handleChangeStatus(row.id, e)}
            disabled={false}
          />
        </div>
      ),
    },
    {
      field: 'actions',
      label: 'Actions',
      align: 'center',
      flex: 2,
      component: (row) => {
        const canDelete = row.is_main === 0;

        return (
          <div className="flex gap-2 items-center justify-center">
            <button
              disabled={!canDelete}
              onClick={() => setShowDeleteModal({ show: true, data: row })}
              className={`${
                canDelete
                  ? 'hover:bg-gray-100 border-error-light'
                  : 'bg-border-light-secondary border-none'
              } p-2 bg-transparent shadow-none cursor-pointer border-solid  rounded border `}
              title={canDelete && 'Delete'}>
              <Icon
                color={canDelete ? '#D02533' : '#CACFD8'}
                src={Icons.trashIcon}
                width={16}
                height={16}
              />
            </button>
          </div>
        );
      },
      sortValue: () => '',
    },
  ];

  const isButtonDisabled = useMemo(() => {
    return !email.trim() || isSaving || isLoadingData;
  }, [email, isSaving, isLoadingData]);

  return (
    <div className="pb-6">
      <div className="mt-6 bg-white">
        <h1 className="text-lg font-bold text-primary-text m-0 mb-2">
          Additional Notification Contacts
        </h1>

        <p className="text-secondary-text text-base m-0">
          Notifications are sent to the primary email and phone number
          associated with your account. You may add additional contacts if you
          want them to receive notifications as well.
        </p>
      </div>
      <div className="flex gap-2  items-end my-6">
        <div className="w-96">
          <Input
            type="text"
            placeholder="Enter your Email"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <IconButton
          text="Add to List"
          onClick={handleSave}
          disable={isButtonDisabled}
          isSaving={isSaving}
          icon="Add"
          color="white"
          style={{
            height: 48,
            borderRadius: 4,
            width: 180,
            backgroundColor: isButtonDisabled ? '#F5F7FA' : '#131517',
            color: isButtonDisabled ? '#CACFD8' : 'white',
            cursor: 'pointer',
            borderColor: isButtonDisabled ? '#CACFD8' : '#131517',
            opacity: isButtonDisabled ? 0.5 : 1,
          }}
        />
      </div>
      <div className="">
        <h1 className="text-lg font-bold text-primary-text m-0 mb-2">
          Recipient Preferences
        </h1>

        <p className="text-secondary-text text-base m-0 mb-6">
          Added contacts appear below and can be managed from this list.
        </p>
        {isLoadingData ? (
          <LoadingV2 />
        ) : (
          <TableV3
            headers={columns}
            data={subUsers}
            emptyMessage=""
            minWidth="1244px"
            stickyHeader={true}
            appHeaderHeight={64}
          />
        )}
      </div>
      {createPortal(
        <Modal
          show={showDeleteModal.show}
          children={
            <div className="flex flex-col items-center justify-center gap-6 w-full">
              <span className="text-primary-text font-bold mt-6">
                Are you sure you want to remove this contact?
              </span>
              <span className="text-secondary-text max-w-480 px-10 mx-auto text-center">
                Once removed, this contact will no longer receive notifications
                from your account.
              </span>
            </div>
          }
          submitText={'Yes, Remove'}
          icon={'deleteIcon'}
          title={'Remove Contact'}
          setShow={() => setShowDeleteModal({ show: false, data: null })}
          submit={handleDelete}
        />,
        document.body
      )}
    </div>
  );
};
